import React, { FC, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { MyAccountContainer } from '@components/organisms/MyAccountContainer';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Form } from '@components/atoms/Form';
import { Button, ButtonMode } from '@components/atoms/Button';
import { InputErrors } from '@types';
import { GetMyAccountAddressesEditBodyInput, useGetMyAccountAddressesEdit } from '@services/useGetMyAccountAddressesEdit';
import { Loader } from '@components/molecules/Pagination';
import { Select, SelectType } from '@components/atoms/Select';
import { Typography } from '@components/atoms/Typography';
import { ErrorMessages } from '@components/atoms/ErrorMessages';
import { SITE_URLS } from '@constants';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useGetAllGeoMunicipalitiesOfDistrict } from '@services/useGetAllGeoMunicipalitiesOfDistrict';
import { useData } from '@hooks/useData';
import { useNotifications } from '@hooks/useNotifications';
import { removeNonLetters } from '@utils/format';
import { Space } from '@components/atoms/Space';
import { useGetAllGeoDistricts } from '@services/useGetAllGeoDistricts';

interface EditAddressFormErrors {
    general: string;
    name: InputErrors;
    lastName: InputErrors;
    address: InputErrors;
    complement: InputErrors;
    district: InputErrors;
    municipality: InputErrors;
    postalCode: InputErrors;
    region: InputErrors;
    otherRegion: InputErrors;
    type: InputErrors;
    primary: InputErrors;
}

export const ManageAddressesEdit: FC = () => {
    const { t } = useTranslation();
    const { uuid } = useParams();
    const { allSettings, addressesDetail, allGeoDistricts, isLoadingAddressesDetail, setAddressesDetail } = useData();
    const { showDefaultNotification } = useNotifications();
    const {
        request: requestGetMyAccountAddressesEdit,
        lastResponseTime: lastResponseTimeEdit,
        errorMessage: errorMessageEdit,
        validationErrors: validationErrorsEdit,
        success: successMessageEdit,
    } = useGetMyAccountAddressesEdit();
    const { request: requestGetAllGeoDistricts, loading: loadingGetAllGeoDistricts } = useGetAllGeoDistricts();
    const { request: requestGetAllGeoMunicipalities, loading: loadingGetAllGeoMunicipalities } =
        useGetAllGeoMunicipalitiesOfDistrict();
    const { navigate } = useCustomNavigate();

    const translations = {
        title: t('address-management-edit-create-component.page.edit-title'),
        typeLabel: t('address-management-edit-create-component.type.label'),
        typeRequiredError: t('address-management-edit-create-component.type.required-error'),
        nameLabel: t('address-management-edit-create-component.name.label'),
        nameRequiredError: t('address-management-edit-create-component.name.required-error'),
        lastNameLabel: t('address-management-edit-create-component.last-name.label'),
        lastNameRequiredError: t('address-management-edit-create-component.last-name.required-error'),
        fiscalNumberLabel: t('address-management-edit-create-component.fiscal-number.label'),
        fiscalNumberRequiredError: t('address-management-edit-create-component.fiscal-number.required-error'),
        addressLabel: t('address-management-edit-create-component.address.label'),
        addressRequiredError: t('address-management-edit-create-component.address.required-error'),
        complementLabel: t('address-management-edit-create-component.complement.label'),
        complementRequiredError: t('address-management-edit-create-component.complement.required-error'),
        districtLabel: t('address-management-edit-create-component.district.label'),
        districtRequiredError: t('address-management-edit-create-component.district.required-error'),
        municipalityRequiredError: t('address-management-edit-create-component.municipality.required-error'),
        postalCodeLabel: t('address-management-edit-create-component.postal-code.label'),
        postalCodeRequiredError: t('address-management-edit-create-component.postal-code.required-error'),
        regionLabel: t('address-management-edit-create-component.region.label'),
        regionRequiredError: t('address-management-edit-create-component.region.required-error'),
        regionOtherOption: t('address-management-edit-create-component.region.other.option'),
        otherRegion: t('address-management-edit-create-component.other-region.label'),
        otherRegionRequiredError: t('address-management-edit-create-component.other-region.required-error'),
        primaryLabel: t('address-management-edit-create-component.primary.label'),
        billing: t('address-management-edit-create-component.select-options.billing'),
        delivery: t('address-management-edit-create-component.select-options.delivery'),
        primaryYes: t('address-management-edit-create-component.select-options.yes'),
        primaryNo: t('address-management-edit-create-component.select-options.no'),
        default: t('select-default-option'),
    };

    const formRef = useRef<HTMLFormElement>(null);
    const [fieldName, setFieldName] = useState<string>('');
    const [fieldLastName, setFieldLastName] = useState<string>('');
    const [fieldFiscalNumber, setFieldFiscalNumber] = useState<string>('');
    const [fieldType, setFieldType] = useState<string>('');
    const [fieldAddress, setFieldAddress] = useState<string>('');
    const [fieldComplement, setFieldComplement] = useState<string>('');
    const [fieldCity, setFieldCity] = useState<string>('');
    const [fieldMunicipality, setFieldMunicipality] = useState<string>('');
    const [fieldPostalCode, setFieldPostalCode] = useState<string>('');
    const [fieldRegion, setFieldRegion] = useState<string>('');
    const [fieldOtherRegion, setFieldOtherRegion] = useState<string>('');
    const [fieldPrimary, setFieldPrimary] = useState<string>('');
    const [disabled, setDisabled] = useState<boolean>(false);

    // districts are filtered by selected region
    const [filteredDistricts, setFilteredDistricts] = useState<{ name?: string; district_code?: string; region_uuid?: string }[]>(
        []
    );
    const [districtAndMunicipalityDisabled, setDistrictAndMunicipalityDisabled] = useState(true);

    useEffect(() => {
        // when unmounting page, reset data
        // to prevent showing old results when returning to this page
        return () => {
            setAddressesDetail(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (addressesDetail) {
            setFieldName(addressesDetail?.data?.name || '');
            setFieldLastName(addressesDetail?.data?.lastName || '');
            setFieldFiscalNumber(addressesDetail?.data?.fiscalNumber || '');
            setFieldType(addressesDetail?.data?.type || '');
            setFieldAddress(addressesDetail?.data?.address || '');
            setFieldComplement(addressesDetail?.data?.complement || '');
            setFieldCity(addressesDetail?.data?.district || '');
            setFieldMunicipality(addressesDetail?.data?.municipality || '');
            setFieldPostalCode(addressesDetail?.data?.postalCode || '');
            setFieldRegion(addressesDetail?.data?.region || '');
            setFieldPrimary(addressesDetail?.data?.primary || '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addressesDetail]);

    useEffect(() => {
        requestGetAllGeoDistricts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (fieldCity) {
            requestGetAllGeoMunicipalities({ district_code: fieldCity });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldCity]);

    // make city options depend on region
    useEffect(() => {
        if (addressesDetail?.data && allGeoDistricts?.data && allGeoDistricts.data.length > 0) {
            const visibleDistricts = allGeoDistricts.data.filter((district) => district.region_uuid === fieldRegion);

            setFilteredDistricts(visibleDistricts);
            const hasVisibleDistricts = visibleDistricts.length > 0;

            setDistrictAndMunicipalityDisabled(!hasVisibleDistricts);

            // reset selection if current selection is not valid
            if (fieldCity && !visibleDistricts.find((district) => district.district_code === fieldCity)) {
                setFieldCity('');
                setFieldMunicipality('');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldRegion, allGeoDistricts?.data]);

    useEffect(() => {
        if (lastResponseTimeEdit) {
            if (errorMessageEdit || validationErrorsEdit) {
                setErrors({
                    general: errorMessageEdit as string,
                    // match form request errors with frontend errors object
                    name: validationErrorsEdit?.recipient_first_name || [],
                    lastName: validationErrorsEdit?.recipient_last_name || [],
                    address: validationErrorsEdit?.address_line1 || [],
                    complement: validationErrorsEdit?.address_line2 || [],
                    postalCode: validationErrorsEdit?.postal_code || [],
                    region: validationErrorsEdit?.region_uuid || [],
                    otherRegion: validationErrorsEdit?.other_region || [],
                    district: validationErrorsEdit?.district_code || [],
                    municipality: validationErrorsEdit?.municipality_code || [],
                    type: validationErrorsEdit?.type || [],
                    primary: validationErrorsEdit?.primary || [],
                });
            } else {
                showDefaultNotification({ type: 'success', message: successMessageEdit || '' });
                navigate(`/${SITE_URLS.myAddresses}`);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTimeEdit]);

    const handleFiscalInput = (value: string) => {
        value = value.replace(/[^0-9]/g, '');

        if (value.length > 9) {
            value = value.slice(0, 8);
        }

        setFieldFiscalNumber(value);
    };

    const handleNameInput = (value: string) => {
        value = removeNonLetters(value);

        setFieldName(value);
    };

    const handleLastNameInput = (value: string) => {
        value = removeNonLetters(value);

        setFieldLastName(value);
    };

    const [errors, setErrors] = useState<EditAddressFormErrors>({
        general: '',
        name: [],
        lastName: [],
        address: [],
        complement: [],
        district: [],
        municipality: [],
        postalCode: [],
        region: [],
        otherRegion: [],
        type: [],
        primary: [],
    });

    // prevent user from submitting the form multiple times with the "other" region
    useEffect(() => {
        if (fieldRegion === 'other' && fieldOtherRegion && errors.otherRegion.length > 0) {
            setDisabled(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldRegion, errors.otherRegion]);

    // reset 'other' region error
    useEffect(() => {
        if (fieldRegion !== 'other') {
            setFieldOtherRegion('');
            setErrors((previous) => {
                return { ...previous, otherRegion: [] };
            });
            setDisabled(false);
        }
    }, [fieldRegion]);

    const handleOnClickSave = () => {
        const errors = validateForm();

        if (
            errors.name.length === 0 &&
            errors.lastName.length === 0 &&
            errors.address.length === 0 &&
            errors.complement.length === 0 &&
            errors.district.length === 0 &&
            errors.municipality.length === 0 &&
            errors.postalCode.length === 0 &&
            errors.region.length === 0 &&
            errors.otherRegion.length === 0 &&
            errors.type.length === 0 &&
            errors.primary.length === 0
        ) {
            formRef?.current?.requestSubmit();
        }
    };

    const validateForm: () => EditAddressFormErrors = () => {
        const newErrors: EditAddressFormErrors = {
            general: '',
            name: [],
            lastName: [],
            address: [],
            complement: [],
            district: [],
            municipality: [],
            postalCode: [],
            region: [],
            otherRegion: [],
            type: [],
            primary: [],
        };

        !fieldName.trim() && newErrors.name.push(translations.nameRequiredError);
        !fieldLastName.trim() && newErrors.lastName.push(translations.lastNameRequiredError);
        !fieldAddress.trim() && newErrors.address.push(translations.addressRequiredError);
        !fieldComplement.trim() && newErrors.complement.push(translations.complementRequiredError);
        !fieldPostalCode.trim() && newErrors.postalCode.push(translations.postalCodeRequiredError);
        !fieldRegion && newErrors.region.push(translations.regionRequiredError);
        !fieldType && newErrors.type.push(translations.typeRequiredError);

        if (fieldRegion === 'other') {
            !fieldOtherRegion.trim() && newErrors.otherRegion.push(translations.otherRegionRequiredError);
        }

        setErrors(newErrors);

        return newErrors;
    };

    // reset postal code errors when changing region
    useEffect(() => {
        setErrors((previous) => {
            return { ...previous, postalCode: [] };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldRegion]);

    const submitForm: () => void = () => {
        const addressData: GetMyAccountAddressesEditBodyInput = {
            recipient_first_name: fieldName,
            recipient_last_name: fieldLastName,
            type: fieldType,
            address_line1: fieldAddress,
            address_line2: fieldComplement,
            municipality_code: fieldMunicipality,
            district_code: fieldCity,
            postal_code: fieldPostalCode,
            region_uuid: fieldRegion !== 'other' ? fieldRegion : undefined,
            other_region: fieldRegion === 'other' ? fieldOtherRegion : undefined,
            fiscal_number: fieldFiscalNumber,
            primary: fieldPrimary,
        };
        requestGetMyAccountAddressesEdit({ uuid: uuid || '' }, addressData);
    };

    return (
        <MyAccountContainer title={translations.title}>
            {isLoadingAddressesDetail ? (
                <Loader />
            ) : (
                <Form formRef={formRef} onSubmit={submitForm}>
                    <div className="d-flex flex-column gap-3">
                        <>
                            {errors.general && (
                                <>
                                    <ErrorMessages messages={[errors.general]} />
                                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                                </>
                            )}
                        </>

                        <div className="row mt-5">
                            <div className="col-sm-6 col-12">
                                {/* Name */}
                                <Input
                                    type={InputTypes.text}
                                    label={translations.nameLabel}
                                    variant={InputVariants.labeledInput}
                                    value={fieldName}
                                    onChange={(value) => handleNameInput(value)}
                                    state={errors.name.length > 0 ? InputStates.error : undefined}
                                    errorMessages={errors.name}
                                />
                                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                            </div>
                            <div className="col-sm-6 col-12">
                                {/* last name */}
                                <Input
                                    type={InputTypes.text}
                                    label={translations.lastNameLabel}
                                    variant={InputVariants.labeledInput}
                                    value={fieldLastName}
                                    onChange={(value) => handleLastNameInput(value)}
                                    state={errors.lastName.length > 0 ? InputStates.error : undefined}
                                    errorMessages={errors.lastName}
                                />
                                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                            </div>
                            <div className="col-12">
                                {/* address */}
                                <Input
                                    type={InputTypes.text}
                                    label={translations.addressLabel}
                                    variant={InputVariants.labeledInput}
                                    value={fieldAddress}
                                    onChange={(value) => setFieldAddress(value)}
                                    state={errors.address.length > 0 ? InputStates.error : undefined}
                                    errorMessages={errors.address}
                                />
                                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                            </div>
                            <div className="col-sm-6 col-12">
                                {/* complement */}
                                <Input
                                    type={InputTypes.text}
                                    label={translations.complementLabel}
                                    variant={InputVariants.labeledInput}
                                    value={fieldComplement}
                                    onChange={(value) => setFieldComplement(value)}
                                    state={errors.complement.length > 0 ? InputStates.error : undefined}
                                    errorMessages={errors.complement}
                                />
                                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                            </div>
                            <div className="col-sm-6 col-12">
                                <Input
                                    type={InputTypes.text}
                                    label={translations.fiscalNumberLabel}
                                    variant={InputVariants.labeledInput}
                                    value={fieldFiscalNumber}
                                    onChange={(value) => handleFiscalInput(value)}
                                />
                                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                            </div>
                            <div className="col-sm-6 col-12 pt-sm-2 mt-sm-1">
                                {/* postalCode */}
                                <Input
                                    type={InputTypes.text}
                                    label={translations.postalCodeLabel}
                                    variant={InputVariants.labeledInput}
                                    value={fieldPostalCode}
                                    onChange={(value) => setFieldPostalCode(value)}
                                    state={errors.postalCode.length > 0 ? InputStates.error : undefined}
                                    errorMessages={errors.postalCode}
                                />
                                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                            </div>
                            <div className="col-sm-6 col-12">
                                {/* region */}
                                <Typography color="muted" fontSize="12">
                                    {translations.regionLabel}
                                </Typography>
                                <Select
                                    classes="w-100"
                                    type={SelectType.variant1}
                                    options={[
                                        { value: '', text: translations.default },
                                        ...(allSettings?.data?.regions || []).map((region) => ({
                                            text: region.name || '',
                                            value: region.uuid || '',
                                        })),
                                        { value: 'other', text: translations.regionOtherOption },
                                    ]}
                                    value={fieldRegion}
                                    onChange={(value) => setFieldRegion(value)}
                                />
                                {errors.region.length > 0 && <ErrorMessages messages={errors.region} />}
                                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                            </div>
                            {fieldRegion === 'other' && (
                                <div className="col-12">
                                    {/* other region */}
                                    <Input
                                        type={InputTypes.text}
                                        label={translations.otherRegion}
                                        variant={InputVariants.labeledInput}
                                        value={fieldOtherRegion}
                                        onChange={(value) => setFieldOtherRegion(value)}
                                        state={errors.otherRegion.length > 0 ? InputStates.error : undefined}
                                        errorMessages={errors.otherRegion}
                                    />
                                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                                </div>
                            )}
                            {/* city / district */}
                            <div className="col-sm-6 col-12">
                                <Typography color="muted" fontSize="12">
                                    {translations.districtLabel}
                                </Typography>
                                <Select
                                    classes="w-100"
                                    type={SelectType.variant1}
                                    options={[
                                        { value: '', text: translations.default },
                                        ...filteredDistricts.map((district) => ({
                                            text: district.name || '',
                                            value: district.district_code || '',
                                        })),
                                    ]}
                                    value={fieldCity}
                                    onChange={(value) => setFieldCity(value)}
                                    disabled={districtAndMunicipalityDisabled || loadingGetAllGeoDistricts}
                                />
                                {errors.district.length > 0 && <ErrorMessages messages={errors.district} />}
                                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                            </div>
                            {/* municipality */}
                            <div className="col-sm-6 col-12">
                                <MunicipalitiesSelect
                                    fieldMunicipality={fieldMunicipality}
                                    setFieldMunicipality={(value: string) => setFieldMunicipality(value)}
                                    disabled={districtAndMunicipalityDisabled || loadingGetAllGeoMunicipalities}
                                    errors={errors.municipality}
                                />
                                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                            </div>
                            <div className="col-sm-6 col-12">
                                {/* type */}
                                <Typography color="muted" fontSize="12">
                                    {translations.typeLabel}
                                </Typography>
                                <Select
                                    classes="w-100"
                                    type={SelectType.variant1}
                                    options={[
                                        { text: translations.delivery, value: 'delivery' },
                                        { text: translations.billing, value: 'billing' },
                                    ]}
                                    value={fieldType}
                                    onChange={(value) => setFieldType(value)}
                                />
                                {errors.type.length > 0 && <ErrorMessages messages={errors.type} />}
                                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                            </div>
                            <div className="col-sm-6 col-12">
                                {/* primary */}
                                <Typography color="muted" fontSize="12">
                                    {translations.primaryLabel}
                                </Typography>
                                <Select
                                    classes="w-100"
                                    type={SelectType.variant1}
                                    options={[
                                        { text: translations.primaryYes, value: 'on' },
                                        { text: translations.primaryNo, value: '' },
                                    ]}
                                    value={fieldPrimary}
                                    onChange={(value) => setFieldPrimary(value)}
                                />
                                {errors.primary.length > 0 && <ErrorMessages messages={errors.primary} />}
                                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                            </div>
                        </div>
                        <Button
                            label={translations.title}
                            classes="w-100"
                            mode={ButtonMode.primary}
                            onClick={handleOnClickSave}
                            disabled={disabled}
                        />
                    </div>
                </Form>
            )}
        </MyAccountContainer>
    );
};

interface MunicipalitiesSelectProps {
    fieldMunicipality: string;
    setFieldMunicipality: (value: string) => void;
    disabled: boolean;
    errors: EditAddressFormErrors['municipality'];
}

const MunicipalitiesSelect: FC<MunicipalitiesSelectProps> = ({ fieldMunicipality, setFieldMunicipality, disabled, errors }) => {
    const { allGeoMunicipalitiesOfDistrict } = useData();
    const { t } = useTranslation();

    const translations = {
        municipalityLabel: t('address-management-edit-create-component.municipality.label'),
    };

    return (
        <>
            <Typography color="muted" fontSize="12">
                {translations.municipalityLabel}
            </Typography>
            <Select
                classes="w-100"
                type={SelectType.variant1}
                options={[
                    { text: '', value: '' },
                    ...(allGeoMunicipalitiesOfDistrict?.data?.map((municipality) => ({
                        text: municipality.name ?? '',
                        value: municipality.municipality_code ?? '',
                    })) || []),
                ]}
                value={fieldMunicipality}
                onChange={(value) => setFieldMunicipality(value)}
                disabled={disabled}
            />
            {errors.length > 0 && <ErrorMessages messages={errors} />}
        </>
    );
};
